import React, { useEffect, useState } from 'react';
import { Typography, Grid } from '@mui/material';
import { di } from '../../../../../../state/inversify.config';
import { AssessmentCreationViewService } from '../../../assessmentCreationView/assessmentCreation.view.service';
import tandemStance from './tandemStance.svg';
import { Timer } from '../../components/timer/timer.dumb';
import { ModulesSpecsDomain } from '../modules.domain';
import { Instructions } from '../../components/instructions/instructions.dumb';
import { SkipModule } from '../../components/skipModule/skipModule.smart';
import { AssessmentModules } from '../../modules';
import { Notes } from '../../components/notes/notes.dumb';
import { useCustomTranslation } from '../../../../../../translation/customTranslation';
import { ErrorCounter } from '../../components/errorCounter/errorCounter';

export function BalanceTandemStance({ domainService }: any) {
  const moduleSpecsDomain = di.get<ModulesSpecsDomain>(ModulesSpecsDomain);
  const { t } = useCustomTranslation('assessment');
  const maxErrorCount: number = 10;
  const countDownTimerInitial: number = 999;
  const countDownTimerValid: number = 20;
  const assessmentCreationViewService = di.get<AssessmentCreationViewService>(
    AssessmentCreationViewService,
  );
  const module = assessmentCreationViewService.useModuleSelector();
  const { balanceTandemStance } = module;
  const moduleName: string = 'balanceTandemStance';
  const [count, setCount] = useState(
    balanceTandemStance?.balanceTandemStanceErrors
      ? Number(balanceTandemStance?.balanceTandemStanceErrors)
      : 0,
  );

  const [time, setTime] = useState<number>(
    balanceTandemStance?.countdownTimer
      ? countDownTimerValid
      : countDownTimerInitial,
  );

  const { heading, instructions, note } =
    moduleSpecsDomain.tandemBalanceExamConfig();

  const saveModule = () => {
    assessmentCreationViewService.dispatchSetAssessmentModule({
      balanceTandemStance: {
        balanceTandemStanceErrors: count,
        countdownTimer: time,
        skipModuleFlag: balanceTandemStance?.skipModuleFlag,
        skipModuleComments: balanceTandemStance?.skipModuleComments,
        NORMATIVE_SCORE: 1,
        balanceTandemStanceNotes: balanceTandemStance?.balanceTandemStanceNotes,
      },
    });
  };

  const onStartStop = () => {
    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'balanceTandemStanceErrors',
      count,
    );

    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  /**
   * For validation, we track this variable countdownTimer - we need it to know:
   * a. that the user started the timer, or
   * b. that the user didn't use the timer but added/subtracted errors
   * Without it, we only have balanceTandemStanceErrors to validate off, and if its value
   * is equal to the default value of 0 we have no way of knowing that the user interacted with this module.
   */
  const changeCountdownTimer = () => {
    if (!time || time === countDownTimerInitial) {
      setTime(countDownTimerValid);
    }
  };

  const incrementCount = () => {
    setCount(count >= maxErrorCount ? count : count + 1);
    changeCountdownTimer();
    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  const decrementCount = () => {
    setCount(count > 0 ? count - 1 : count);
    changeCountdownTimer();
    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  const resetCount = () => {
    setCount(0);
    setTime(countDownTimerValid);
    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  const onTimeChange = (newTime: number) => {
    setTime(newTime);
  };

  const setNotesComments = (key: string, value: string): void => {
    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      key,
      value,
    );

    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  useEffect(() => {
    saveModule();

    domainService.dispatchBackEnabled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, time]);

  const skipModuleOnChange = (moduleSkipped: boolean) => {
    if (moduleSkipped) {
      setCount(0);
      setTime(countDownTimerValid);
    }
  };

  return (
    <>
      <Typography variant="h6" fontWeight="bold">
        {t(heading)}
      </Typography>
      <Instructions instructions={instructions} />
      <Grid display="flex" justifyContent="center">
        <Grid
          item
          justifyContent="center"
          textAlign="center"
          sx={{
            my: 2,
            p: 2,
            boxShadow:
              '0px 10.5px 36px rgba(0, 0, 0, 0.039), 0px 3px 13px rgba(0, 0, 0, 0.19);',
          }}
        >
          <Grid item justifyContent="center">
            <Timer
              onStartStop={onStartStop}
              onReset={resetCount}
              onTimeChange={onTimeChange}
            />
          </Grid>
          <Grid
            item
            justifyContent="center"
            sx={{
              p: 1,
            }}
          >
            <ErrorCounter
              errorCount={
                balanceTandemStance?.balanceTandemStanceErrors || count
              }
              onAdd={incrementCount}
              onMinus={decrementCount}
            />
          </Grid>
        </Grid>
        <Grid sx={{ mx: 2, my: 3, userSelect: 'none' }}>
          <img src={tandemStance} alt="" />
        </Grid>
      </Grid>
      {note && (
        <Notes
          label={t(note.label)}
          keyValue={note.key}
          onSave={setNotesComments}
          data={balanceTandemStance?.balanceTandemStanceNotes || ''}
          fullWidth
        />
      )}
      <SkipModule
        moduleKeyValue={moduleName}
        skipModuleFlag={balanceTandemStance?.skipModuleFlag}
        skipModuleComments={balanceTandemStance?.skipModuleComments}
        onChange={skipModuleOnChange}
      />
    </>
  );
}
